import React, { useRef, useState, useEffect } from "react";
import { useTheme } from "@mui/material";
import styled from "@xstyled/styled-components";
import { tokens } from "../../utils/theme";
import { useUpcomingEventsData } from "../../context/UpcomingEventsContext";
import { copyTableAsJSON } from "../../utils/clipboardUtils";

import {
    Panel,
    PanelHeader,
    PanelTitle,
    TableWrapper,
    Table,
    Thead,
    Th,
    Tr,
    Td,
    TdSmall,
    ScrollRail,
    ScrollThumb,
    ScrollRailVertical,
    ScrollThumbVertical,
    Btn,
    BtnIcon
} from "../../components/SharedPageStyles";
import CustomSelect from "../../components/CustomSelect";

const ImpactBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
        if (props.level?.includes("Very High") || props.level?.includes("High")) return "rgba(244, 67, 54, 0.15)";
        if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.15)";
        return "rgba(76, 175, 80, 0.15)";
    }};
  color: ${props => {
        if (props.level?.includes("Very High") || props.level?.includes("High")) return tokens(props.theme.palette.mode).redAccent[500];
        if (props.level?.includes("Medium")) return tokens(props.theme.palette.mode).orangeAccent[500];
        return tokens(props.theme.palette.mode).greenAccent[500];
    }};
  border: 1px solid ${props => {
        if (props.level?.includes("Very High") || props.level?.includes("High")) return "rgba(244, 67, 54, 0.3)";
        if (props.level?.includes("Medium")) return "rgba(255, 152, 0, 0.3)";
        return "rgba(76, 175, 80, 0.3)";
    }};
`;





const EventText = styled.span`
  font-weight: 600;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
`;

const EditableInput = styled.input`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const EditableTextArea = styled.textarea`
  width: 100%;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.7)'};
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  color: inherit;
  padding: 4px;
  padding-right: 36px; /* Space for arrows */
  border-radius: 4px;
  font-family: inherit;
  font-size: inherit;
  resize: vertical;
  min-height: 60px;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  &:focus {
    outline: none;
    border-color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}33;
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ScrollControls = styled.div`
  position: absolute;
  right: 4px;
  top: 4px;
  bottom: 4px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2px;
  pointer-events: none;
`;

const ScrollButton = styled.div`
  pointer-events: auto;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 2px;
  transition: all 0.2s;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)'};

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).blueAccent[500]};
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.95)'};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
`;

const ModalContent = styled.div`
  background: #1e2130;
  padding: 24px;
  border-radius: 8px;
  width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
`;

const CheckboxContainer = styled.div`
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clippath: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : 'transparent'};
  border: 2px solid ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[500]
        : tokens(props.theme.palette.mode).grey[500]};
  border-radius: 4px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px ${props => tokens(props.theme.palette.mode).blueAccent[500]}40; 
  }

  &:hover {
     border-color: ${props => props.checked || props.indeterminate
        ? tokens(props.theme.palette.mode).blueAccent[600]
        : tokens(props.theme.palette.mode).blueAccent[400]};
  }
`;

const Icon = styled.svg`
  fill: none;
  stroke: ${props => props.theme.palette.mode === 'dark' ? '#000' : '#fff'};
  stroke-width: 3px;
  width: 12px;
  height: 12px;
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
`;

const CustomCheckbox = ({ checked, indeterminate, onChange, ...props }) => (
    <CheckboxContainer onClick={(e) => {
        e.stopPropagation();
        onChange({ target: { checked: !checked } });
    }}>
        <HiddenCheckbox checked={checked} onChange={onChange} {...props} />
        <StyledCheckbox checked={checked} indeterminate={indeterminate}>
            {indeterminate ? (
                <Icon viewBox="0 0 24 24" visible={true}>
                    <line x1="4" y1="12" x2="20" y2="12" />
                </Icon>
            ) : (
                <Icon viewBox="0 0 24 24" visible={checked}>
                    <polyline points="20 6 9 17 4 12" />
                </Icon>
            )}
        </StyledCheckbox>
    </CheckboxContainer>
);

const EditableCell = ({ value, onChange, type = "text" }) => {
    const [localValue, setLocalValue] = useState(value);
    const [showScroll, setShowScroll] = useState(false);
    const textareaRef = useRef(null);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    useEffect(() => {
        if (textareaRef.current) {
            const { scrollHeight, clientHeight } = textareaRef.current;
            setShowScroll(scrollHeight > clientHeight);
        }
    }, [localValue]);

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleScroll = (direction) => {
        if (textareaRef.current) {
            textareaRef.current.scrollTop += direction * 20;
        }
    };

    if (type === "textarea") {
        return (
            <TextAreaWrapper>
                <EditableTextArea
                    ref={textareaRef}
                    value={localValue}
                    onChange={(e) => setLocalValue(e.target.value)}
                    onBlur={handleBlur}
                    onClick={(e) => e.stopPropagation()}
                />
                {showScroll && (
                    <ScrollControls>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(-1); }} title="Scroll Up">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" />
                            </svg>
                        </ScrollButton>
                        <ScrollButton onClick={(e) => { e.stopPropagation(); handleScroll(1); }} title="Scroll Down">
                            <svg viewBox="0 0 24 24">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                            </svg>
                        </ScrollButton>
                    </ScrollControls>
                )}
            </TextAreaWrapper>
        );
    }
    return (
        <EditableInput
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            onClick={(e) => e.stopPropagation()}
        />
    );
};

const UpcomingEventsTable = ({ data, title, defaultCategory, onReorder }) => {
    const theme = useTheme();
    const { filteredRows, deleteRow, updateRow, addRow, impactFilter, setImpactFilter, getOriginalIndex } = useUpcomingEventsData();

    // Use passed data or fallback to context filtered rows
    const rows = data || filteredRows;

    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [rowsToDelete, setRowsToDelete] = useState(null);

    const tableWrapperRef = useRef(null);
    const scrollRailXRef = useRef(null);
    const scrollRailYRef = useRef(null);

    const [showScrollX, setShowScrollX] = useState(false);
    const [showScrollY, setShowScrollY] = useState(false);
    const [thumbWidth, setThumbWidth] = useState(0);
    const [thumbHeight, setThumbHeight] = useState(0);
    const [thumbLeft, setThumbLeft] = useState(0);
    const [thumbTop, setThumbTop] = useState(0);

    const handleEditModeToggle = () => {
        setIsEditMode(!isEditMode);
        setSelectedRows(new Set());
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all indices based on current rows
            const allIndices = new Set(rows.map((_, index) => index));
            setSelectedRows(allIndices);
        } else {
            setSelectedRows(new Set());
        }
    };

    const handleRowClick = (index) => {
        if (!isEditMode) return;
        const newSelected = new Set(selectedRows);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedRows(newSelected);
    };

    const handleDeleteSelected = () => {
        const selectedIndices = Array.from(selectedRows).sort((a, b) => b - a);
        const toDelete = selectedIndices.map(index => rows[index]);
        setRowsToDelete(toDelete);
    };

    const confirmDelete = async () => {
        if (!rowsToDelete) return;

        const selectedIndices = Array.from(selectedRows).sort((a, b) => b - a);

        // Delete sequentially from highest index to lowest to avoid index shifting issues
        for (const index of selectedIndices) {
            await deleteRow(index);
        }

        setRowsToDelete(null);
        setSelectedRows(new Set());
    };

    const handleSave = () => {
        setIsEditMode(false);
        setSelectedRows(new Set());
        // Changes are already applied to context state via updateRow
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setSelectedRows(new Set());
        window.location.reload();
    };

    const handleCellUpdate = (index, field, value) => {
        updateRow(index, { [field]: value });
    };

    // --- Scroll Logic ---
    const updateScrollState = () => {
        if (tableWrapperRef.current) {
            const { clientWidth, scrollWidth, clientHeight, scrollHeight, scrollLeft, scrollTop } = tableWrapperRef.current;

            setShowScrollX(scrollWidth > clientWidth);
            setShowScrollY(scrollHeight > clientHeight);

            if (scrollWidth > clientWidth) {
                const thumbW = Math.max((clientWidth / scrollWidth) * clientWidth, 30);
                setThumbWidth(thumbW);
                const maxScrollLeft = scrollWidth - clientWidth;
                const maxThumbLeft = clientWidth - thumbW;
                setThumbLeft((scrollLeft / maxScrollLeft) * maxThumbLeft);
            }

            if (scrollHeight > clientHeight) {
                const thumbH = Math.max((clientHeight / scrollHeight) * clientHeight, 30);
                setThumbHeight(thumbH);
                const maxScrollTop = scrollHeight - clientHeight;
                const maxThumbTop = clientHeight - thumbH;
                setThumbTop((scrollTop / maxScrollTop) * maxThumbTop);
            }
        }
    };

    useEffect(() => {
        updateScrollState();
        window.addEventListener('resize', updateScrollState);
        return () => window.removeEventListener('resize', updateScrollState);
    }, [rows, isEditMode]);

    const handleScroll = () => {
        updateScrollState();
    };

    const handleMouseDownX = (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startLeft = thumbLeft;
        const { clientWidth, scrollWidth } = tableWrapperRef.current;
        const maxScrollLeft = scrollWidth - clientWidth;
        const maxThumbLeft = clientWidth - thumbWidth;

        const onMouseMove = (e) => {
            const deltaX = e.clientX - startX;
            const newThumbLeft = Math.min(Math.max(0, startLeft + deltaX), maxThumbLeft);
            const newScrollLeft = (newThumbLeft / maxThumbLeft) * maxScrollLeft;
            tableWrapperRef.current.scrollLeft = newScrollLeft;
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const handleMouseDownY = (e) => {
        e.preventDefault();
        const startY = e.clientY;
        const startTop = thumbTop;
        const { clientHeight, scrollHeight } = tableWrapperRef.current;
        const maxScrollTop = scrollHeight - clientHeight;
        const maxThumbTop = clientHeight - thumbHeight;

        const onMouseMove = (e) => {
            const deltaY = e.clientY - startY;
            const newThumbTop = Math.min(Math.max(0, startTop + deltaY), maxThumbTop);
            const newScrollTop = (newThumbTop / maxThumbTop) * maxScrollTop;
            tableWrapperRef.current.scrollTop = newScrollTop;
        };

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    // Helper to extract Paris Date and Time parts
    const getParisDateTimeParts = (dateStr) => {
        if (!dateStr) return { dateKey: "Unknown Date", dateHeader: "Unknown Date", timeStr: "--:--" };
        const str = String(dateStr).trim();
        if (!str || str === "--" || str === "N/A" || str === "-") {
            return { dateKey: "Unknown Date", dateHeader: "Unknown Date", timeStr: "--:--" };
        }

        const lower = str.toLowerCase();
        if (lower.includes("market") || lower === "all day" || lower === "tentative") {
            return { dateKey: "Market Hours", dateHeader: "Market Hours", timeStr: str };
        }

        const simpleTimeMatch = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
        if (simpleTimeMatch) {
            const h = simpleTimeMatch[1].padStart(2, '0');
            const m = simpleTimeMatch[2];
            const timeFormatted = (h === "00" && m === "00") ? "_" : `${h}:${m}`;
            return { dateKey: "Today", dateHeader: "Today", timeStr: timeFormatted };
        }

        const ampmMatch = str.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
        if (ampmMatch) {
            let hours = parseInt(ampmMatch[1], 10);
            const minutes = ampmMatch[2];
            const isPm = ampmMatch[3].toLowerCase() === 'pm';
            if (isPm && hours < 12) hours += 12;
            if (!isPm && hours === 12) hours = 0;
            const timeFormatted = `${String(hours).padStart(2, '0')}:${minutes}`;
            return { dateKey: "Today", dateHeader: "Today", timeStr: timeFormatted };
        }

        try {
            let date = new Date(str);
            if (isNaN(date.getTime()) && str.includes(' ')) {
                date = new Date(str.replace(' ', 'T'));
            }

            if (!isNaN(date.getTime())) {
                const hasTz = /Z|[+-]\d{2}(?::?\d{2})?$/.test(str);

                if (hasTz) {
                    const dateKey = date.toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
                    const dateHeader = date.toLocaleDateString('en-US', {
                        timeZone: 'Europe/Paris',
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                    });
                    const timeFormatted = date.toLocaleTimeString('en-GB', {
                        timeZone: 'Europe/Paris',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false
                    });
                    return {
                        dateKey,
                        dateHeader,
                        timeStr: timeFormatted === "00:00" ? "_" : timeFormatted
                    };
                } else {
                    const isoDateMatch = str.match(/^(\d{4}-\d{2}-\d{2})/);
                    const isoTimeMatch = str.match(/T(\d{1,2}):(\d{2})/);

                    let timeFormatted = "--:--";
                    if (isoTimeMatch) {
                        const h = isoTimeMatch[1].padStart(2, '0');
                        const m = isoTimeMatch[2];
                        timeFormatted = (h === "00" && m === "00") ? "_" : `${h}:${m}`;
                    } else {
                        const t = date.toLocaleTimeString('en-GB', {
                            timeZone: 'Europe/Paris',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });
                        timeFormatted = t === "00:00" ? "_" : t;
                    }

                    const dateKey = isoDateMatch ? isoDateMatch[1] : date.toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });
                    const dateHeader = date.toLocaleDateString('en-US', {
                        timeZone: 'Europe/Paris',
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric'
                    });

                    return { dateKey, dateHeader, timeStr: timeFormatted };
                }
            }
        } catch (e) {}

        return { dateKey: str.substring(0, 10), dateHeader: str.substring(0, 10), timeStr: str };
    };

    // Sort rows by Time (Paris chronology)
    const sortedRows = React.useMemo(() => {
        return [...rows].sort((a, b) => {
            const timeA = a.Time || '';
            const timeB = b.Time || '';
            const dateA = new Date(timeA);
            const dateB = new Date(timeB);
            if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
                return dateA - dateB;
            }
            return timeA.localeCompare(timeB);
        });
    }, [rows]);

    const formatDateHeader = (dateStr) => {
        return getParisDateTimeParts(dateStr).dateHeader;
    };

    const formatTime = (dateStr) => {
        return getParisDateTimeParts(dateStr).timeStr;
    };

    // Grouping logic in Europe/Paris
    const groupedRows = [];
    let lastDateKey = null;

    sortedRows.forEach((row, index) => {
        const { dateKey, dateHeader } = getParisDateTimeParts(row.Time);

        if (dateKey !== lastDateKey) {
            groupedRows.push({ type: 'header', date: row.Time, dateHeader, id: `header-${index}` });
            lastDateKey = dateKey;
        }
        groupedRows.push({ type: 'row', data: row, originalIndex: index });
    });

    const DateHeader = styled.tr`
      background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'};
      & td {
        padding: 12px 16px;
        font-weight: 700;
        color: ${props => tokens(props.theme.palette.mode).blueAccent[400]};
        font-size: 14px;
        border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
      }
    `;

    return (
        <Panel>

            <PanelHeader>
                <PanelTitle>{title}</PanelTitle>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '180px' }}>
                        <CustomSelect
                            value={impactFilter}
                            onChange={setImpactFilter}
                            options={[
                                "All",
                                "Very High",
                                "High",
                                "Medium",
                                "Low"
                            ]}
                        />
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <BtnIcon onClick={() => copyTableAsJSON(rows)} title="Copy Table JSON">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '18px', height: '18px' }}>
                                <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                        </BtnIcon>
                        {isEditMode ? (
                            <>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={() => addRow(defaultCategory)} style={{ width: '30px', padding: 0 }}>+</Btn>
                                <Btn
                                    danger
                                    onClick={handleDeleteSelected}
                                    disabled={selectedRows.size === 0}
                                    style={{
                                        minWidth: '30px',
                                        padding: '0 8px',
                                        opacity: selectedRows.size === 0 ? 0.5 : 1,
                                        cursor: selectedRows.size === 0 ? 'not-allowed' : 'pointer',
                                        background: selectedRows.size > 0 ? '#d32f2f' : undefined,
                                        borderColor: selectedRows.size > 0 ? '#b71c1c' : undefined,
                                        color: selectedRows.size > 0 ? '#fff' : undefined
                                    }}
                                >
                                    - {selectedRows.size > 0 && `(${selectedRows.size})`}
                                </Btn>
                                <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />
                                <Btn primary onClick={handleSave}>
                                    Save
                                </Btn>
                                <Btn danger onClick={handleCancel}>
                                    Cancel
                                </Btn>
                            </>
                        ) : (
                            <BtnIcon onClick={handleEditModeToggle} title="Edit Mode">
                                ✎
                            </BtnIcon>
                        )}
                    </div>
                </div>
            </PanelHeader>

            <TableWrapper ref={tableWrapperRef} onScroll={handleScroll}>
                <Table>
                    <Thead>
                        <tr>
                            {isEditMode && (
                                <Th style={{ width: '40px', textAlign: 'center' }}>
                                    <CustomCheckbox
                                        checked={rows.length > 0 && selectedRows.size === rows.length}
                                        indeterminate={selectedRows.size > 0 && selectedRows.size < rows.length}
                                        onChange={handleSelectAll}
                                    />
                                </Th>
                            )}
                            {isEditMode && <Th style={{ width: '50px' }}>ID</Th>}
                            <Th style={{ width: '90px', minWidth: '90px' }}>Time</Th>
                            <Th style={{ width: '180px', minWidth: '180px' }}>Event</Th>
                            <Th style={{ minWidth: '450px' }}>Headline</Th>
                            <Th style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>Impact</Th>
                            <Th style={{ minWidth: '440px' }}>Interpretation</Th>
                            <Th style={{ width: '140px', minWidth: '140px' }}>Key Assets</Th>
                        </tr>
                    </Thead>
                    {/* DragDropContext disabled for grouped view */}
                    <tbody>
                        {groupedRows.length > 0 ? (
                            groupedRows.map((item, i) => {
                                if (item.type === 'header') {
                                    return (
                                        <DateHeader key={item.id}>
                                            <td colSpan={isEditMode ? 8 : 6}>
                                                {item.dateHeader || formatDateHeader(item.date)}
                                            </td>
                                        </DateHeader>
                                    );
                                }

                                const row = item.data;
                                const index = getOriginalIndex(row); // Use original index for actions

                                return (
                                    <Tr
                                        key={row.id || i}
                                        onClick={() => handleRowClick(index)}
                                        style={{
                                            background: isEditMode && selectedRows.has(index) ? 'rgba(128, 203, 196, 0.1)' : 'transparent',
                                            cursor: isEditMode ? 'pointer' : 'default',
                                            display: 'table-row'
                                        }}
                                    >
                                        {isEditMode && (
                                            <Td style={{ width: '40px', textAlign: 'center' }}>
                                                <CustomCheckbox
                                                    checked={selectedRows.has(index)}
                                                    onChange={() => handleRowClick(index)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </Td>
                                        )}
                                        {isEditMode && (
                                            <Td style={{ width: '50px' }}>
                                                <TdSmall style={{ opacity: 0.5 }}>{row.id}</TdSmall>
                                            </Td>
                                        )}
                                        <Td style={{ width: '90px', minWidth: '90px' }}>
                                            {isEditMode ? (
                                                <EditableCell value={row.Time} onChange={(val) => handleCellUpdate(index, 'Time', val)} />
                                            ) : (
                                                <TdSmall>{formatTime(row.Time)}</TdSmall>
                                            )}
                                        </Td>

                                        <Td style={{ width: '180px', minWidth: '180px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {isEditMode ? (
                                                    <>
                                                        <EditableCell value={row.Event} onChange={(val) => handleCellUpdate(index, 'Event', val)} />
                                                        <EditableCell value={row.Tag} onChange={(val) => handleCellUpdate(index, 'Tag', val)} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <EventText style={{ fontSize: '12px' }}>{row.Event}</EventText>
                                                        {row.Tag && <span style={{ fontSize: '10px', opacity: 0.8 }}>{row.Tag}</span>}
                                                    </>
                                                )}
                                            </div>
                                        </Td>
                                        <Td style={{ minWidth: '450px', whiteSpace: 'normal' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                {isEditMode ? (
                                                    <>
                                                        <EditableCell value={row.Headline} onChange={(val) => handleCellUpdate(index, 'Headline', val)} />
                                                        <EditableCell type="textarea" value={row.Details} onChange={(val) => handleCellUpdate(index, 'Details', val)} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <span style={{ fontWeight: 600, color: 'inherit' }}>{row.Headline}</span>
                                                        <span style={{ fontSize: '11px', opacity: 0.8 }}>{row.Details}</span>
                                                    </>
                                                )}
                                            </div>
                                        </Td>
                                        <Td style={{ width: '85px', minWidth: '85px', textAlign: 'center' }}>
                                            {isEditMode ? (
                                                <EditableCell value={row.Impact} onChange={(val) => handleCellUpdate(index, 'Impact', val)} />
                                            ) : (
                                                <ImpactBadge level={row.Impact}>{row.Impact}</ImpactBadge>
                                            )}
                                        </Td>
                                        <Td style={{ minWidth: '440px', whiteSpace: 'normal' }}>
                                            {isEditMode ? (
                                                <EditableCell type="textarea" value={row.Interpretation} onChange={(val) => handleCellUpdate(index, 'Interpretation', val)} />
                                            ) : (
                                                <span style={{ color: 'inherit' }}>{row.Interpretation}</span>
                                            )}
                                        </Td>

                                        <Td style={{ width: '140px', minWidth: '140px', color: theme.palette.mode === 'dark' ? '#82aaff' : '#0288d1', whiteSpace: 'normal' }}>
                                            {isEditMode ? (
                                                <EditableCell value={row.Assets} onChange={(val) => handleCellUpdate(index, 'Assets', val)} />
                                            ) : (
                                                <span style={{ color: 'inherit' }}>{row.Assets}</span>
                                            )}
                                        </Td>
                                    </Tr>
                                );
                            })
                        ) : (
                            <Tr>
                                <Td colSpan={isEditMode ? 8 : 6} style={{ textAlign: "center", padding: "20px", color: "#607d8b" }}>
                                    No upcoming events found.
                                </Td>
                            </Tr>
                        )}
                    </tbody>
                </Table>
            </TableWrapper>

            {showScrollX && (
                <ScrollRail ref={scrollRailXRef}>
                    <ScrollThumb
                        width={`${thumbWidth}px`}
                        left={`${thumbLeft}px`}
                        onMouseDown={handleMouseDownX}
                    />
                </ScrollRail>
            )}

            {showScrollY && (
                <ScrollRailVertical ref={scrollRailYRef}>
                    <ScrollThumbVertical
                        height={`${thumbHeight}px`}
                        top={`${thumbTop}px`}
                        onMouseDown={handleMouseDownY}
                    />
                </ScrollRailVertical>
            )}

            {rowsToDelete && (
                <ModalOverlay>
                    <ModalContent>
                        <h3 style={{ color: '#fff', marginTop: 0 }}>Confirm Deletion</h3>
                        <p style={{ color: '#b0bec5' }}>
                            Are you sure you want to delete {rowsToDelete.length} row{rowsToDelete.length > 1 ? 's' : ''}?
                            This action cannot be undone.
                        </p>
                        <ButtonRow>
                            <Btn onClick={() => setRowsToDelete(null)}>Cancel</Btn>
                            <Btn primary onClick={confirmDelete} style={{ background: '#ef5350', borderColor: '#ef5350' }}>Delete</Btn>
                        </ButtonRow>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Panel>
    );
};

export default UpcomingEventsTable;
